import type { RoiInputs, RoiProject } from '@wr/shared';
import { ApiError, patch } from '../../api.js';
import { openStore } from '../../offline/local.js';

/**
 * ROI offline (decision of Oct 28, 2026): the calculation runs on the
 * device; a change the server has not received yet waits on the device
 * (encrypted, in the person's store) and goes when the network returns. A
 * project opened once is kept on the device, so it opens again offline.
 * Creating a project needs the network.
 */

export interface RoiPendingChange {
  inputs: Partial<RoiInputs>;
  openSections?: number[];
  changedAt: string;
}

type Pending = Record<string, RoiPendingChange>;

const PENDING = 'roi:pending';
const projectKey = (id: string) => `roi:project:${id}`;

/** When this browser keeps nothing on the device, changes wait in memory only. */
let memory: Pending = {};

async function store(userId: string) {
  try {
    return await openStore(userId);
  } catch {
    return null;
  }
}

async function readPending(userId: string): Promise<Pending> {
  const s = await store(userId);
  return s ? ((await s.getMeta<Pending>(PENDING)) ?? {}) : memory;
}

async function writePending(userId: string, pending: Pending): Promise<void> {
  const s = await store(userId);
  if (s) await s.setMeta(PENDING, pending);
  else memory = pending;
}

/** Merges a change into what waits for this project: the newest value of each field. */
export async function queueChange(
  userId: string,
  projectId: string,
  change: RoiPendingChange,
): Promise<void> {
  const pending = await readPending(userId);
  const before = pending[projectId];
  pending[projectId] = {
    inputs: { ...before?.inputs, ...change.inputs },
    openSections: change.openSections ?? before?.openSections,
    changedAt: change.changedAt,
  };
  await writePending(userId, pending);
}

export async function pendingFor(
  userId: string,
  projectId: string,
): Promise<RoiPendingChange | null> {
  return (await readPending(userId))[projectId] ?? null;
}

export type SendOutcome =
  | { status: 'sent'; project: RoiProject }
  | { status: 'waiting' }
  | { status: 'refused'; error: ApiError };

/** No network, or the server failing: the change waits on the device. */
const mustWait = (error: unknown) =>
  !(error instanceof ApiError) || error.code === 'offline' || error.status >= 500;

/**
 * Sends what waits for one project. Refused by the server (the project was
 * deleted, or the person may no longer change it): the change is dropped and
 * the screen says so; nothing is dropped silently.
 */
export async function sendPending(userId: string, projectId: string): Promise<SendOutcome> {
  const change = await pendingFor(userId, projectId);
  if (!change) return { status: 'waiting' };
  try {
    const body: Record<string, unknown> = { changedAt: change.changedAt };
    if (Object.keys(change.inputs).length > 0) body.inputs = change.inputs;
    if (change.openSections) body.openSections = change.openSections;
    const result = await patch<{ project: RoiProject }>(`/roi/projects/${projectId}`, body);
    await settle(userId, projectId, change);
    await keepProject(userId, result.project);
    return { status: 'sent', project: result.project };
  } catch (error) {
    if (mustWait(error)) return { status: 'waiting' };
    await settle(userId, projectId, change);
    return { status: 'refused', error: error as ApiError };
  }
}

/** Removes what was sent, unless a newer change came in meanwhile. */
async function settle(userId: string, projectId: string, sent: RoiPendingChange) {
  const pending = await readPending(userId);
  if (pending[projectId]?.changedAt === sent.changedAt) {
    delete pending[projectId];
    await writePending(userId, pending);
  }
}

/** Sends everything waiting, for every project (the ROI screen calls it when it opens and when the network returns). */
export async function sendAllPending(userId: string): Promise<number> {
  const ids = Object.keys(await readPending(userId));
  let waiting = 0;
  for (const id of ids) {
    if ((await sendPending(userId, id)).status === 'waiting') waiting += 1;
  }
  return waiting;
}

export async function keepProject(userId: string, project: RoiProject): Promise<void> {
  const s = await store(userId);
  if (s) await s.setMeta(projectKey(project.id), project);
}

export async function keptProject(userId: string, id: string): Promise<RoiProject | null> {
  const s = await store(userId);
  return s ? s.getMeta<RoiProject>(projectKey(id)) : null;
}
